import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Check, AlertCircle } from 'lucide-react';

const UpgradeAdmin = () => {
  const { user, login, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleUpgrade = async () => {
    setUpgrading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/upgrade-to-admin`);
      
      // Update local storage and context with new user data
      login(response.data.token, response.data.user);
      
      setSuccess(true);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upgrade account');
    } finally {
      setUpgrading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                Please Log In
              </h3>
              <p className="text-slate-600">
                You need to be logged in to access this page.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (user.email !== 'jason@westlandfmc.com') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                Access Restricted
              </h3>
              <p className="text-slate-600">
                This upgrade feature is only available for Jason Owens.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Card className="border-t-4 border-t-green-500">
            <CardContent className="pt-6 text-center py-12">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-800 mb-2">
                Admin Access Granted!
              </h3>
              <p className="text-slate-600 mb-4">
                Your account has been successfully upgraded to admin.
              </p>
              <p className="text-sm text-slate-500">
                Redirecting to dashboard...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="border-t-4 border-t-[#2B6B7F]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#2B6B7F]" />
              <div>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Upgrade to Admin
                </CardTitle>
                <CardDescription>
                  Get admin privileges for your account
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-slate-700 mb-2">
                <strong>Current Status:</strong>
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Email:</span>
                <span className="text-sm font-semibold text-slate-800">{user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Role:</span>
                <span className="text-sm font-semibold text-slate-800">{user.role || 'member'}</span>
              </div>
            </div>

            {user.role === 'admin' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-700 font-semibold">
                  You already have admin privileges!
                </p>
                <p className="text-sm text-green-600 mt-1">
                  Check the Menu dropdown to access admin features.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <p className="text-slate-700">
                    Click the button below to upgrade your account to admin. This will give you access to:
                  </p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-[#2B6B7F] font-bold">•</span>
                      <span>Admin Dashboard - View all user progress</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#2B6B7F] font-bold">•</span>
                      <span>Broadcast Messaging - Send messages to all users</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#2B6B7F] font-bold">•</span>
                      <span>Additional admin features and controls</span>
                    </li>
                  </ul>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">Error</p>
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleUpgrade}
                  disabled={upgrading}
                  className="w-full bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                  size="lg"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {upgrading ? 'Upgrading...' : 'Upgrade to Admin'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UpgradeAdmin;
